import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Sparkles } from 'lucide-react';
import { data } from '../../nucleo/formato';
import type { Idioma } from '../../nucleo/formato';
import { useOpcoes } from '../../nucleo/notificacoes/dados';
import { useSessao } from '../../nucleo/sessao';
import { NOVIDADES, novidadesPendentes, type Novidade } from '../../novidades';
import { marcarNovidadesVistas, ModalNovidades } from '../../novidades/PopupNovidades';
import { Selo } from '../base';
import { TextoCortado } from '../texto-cortado';

/** Quantas novidades o card lista (as mais novas); "Ver todas" abre o carrossel com todas. */
export const NOVIDADES_NO_CARD = 3;

/**
 * Novidades que esta pessoa pode ver (módulo instalado e permissão), sem as correções, da mais nova para a mais antiga
 * (a ordem do catálogo). Função pura para teste.
 */
export function novidadesVisiveis(lista: readonly Novidade[], p: { instalado: (slug: string) => boolean; pode: (permissao: string) => boolean }): Novidade[] {
  return lista.filter((n) => n.tipo !== 'correcao' && (!n.modulo || p.instalado(n.modulo)) && (!n.permissao || p.pode(n.permissao)));
}

/**
 * Fase 2 (W21, K7; análise A 5.10, seção Sistema): card "Novidades" do Início. Lê o catálogo de novidades do web (o
 * mesmo do pop-up) e as vistas da pessoa (`/notificacoes/opcoes`): quantas ainda não viu e as três mais novas. Cada uma
 * abre o diálogo das novidades nela (com "Ver treinamento" para a aula e "Ir para a tela"); "Ver todas" abre o carrossel
 * inteiro. O que a pessoa vê no diálogo fica marcado como visto. Mesmo cartão dos indicadores (ícone em acento suave,
 * etiqueta "Agora"), sem cálculo no servidor.
 */
export function CartaoNovidades() {
  const { t, i18n } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const opcoes = useOpcoes();
  const [aberto, setAberto] = useState<{ lista: Novidade[]; inicio: number } | null>(null);
  const idioma = (['pt', 'en', 'es'].includes(i18n.language) ? i18n.language : 'pt') as Idioma;
  const visiveis = useMemo(() => novidadesVisiveis(NOVIDADES, { instalado: s.instalado, pode: s.pode }), [s.instalado, s.pode]);
  const pendentes = useMemo(
    () => new Set(opcoes.data ? novidadesPendentes(visiveis, { vistas: opcoes.data.novidades_vistas, usuarioCriadoEm: opcoes.data.usuario_criado_em, instalado: s.instalado, pode: s.pode }).map((n) => n.id) : []),
    [opcoes.data, visiveis, s.instalado, s.pode],
  );
  const marcar = useMutation({
    mutationFn: (ids: string[]) => marcarNovidadesVistas(ids),
    onSettled: () => qc.invalidateQueries({ queryKey: ['notificacoes'] }),
  });
  if (!visiveis.length) return null;
  const rotulo = t('inicio.cards.novidades.rotulo');
  const novas = pendentes.size;
  return (
    <article data-indicador="novidades" aria-label={rotulo} className="flex min-w-0 flex-col rounded-xl border border-borda bg-sup-1 p-4">
      <header className="flex min-w-0 items-start gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-acento-suave text-acento-forte">
          <Sparkles className="size-4" aria-hidden />
        </span>
        <h3 className="min-w-0 flex-1 pt-1.5">
          <TextoCortado linhas={2} className="kicker">{rotulo}</TextoCortado>
        </h3>
        <Selo>{t('inicio.etiqueta.agora')}</Selo>
      </header>
      <div className="min-w-0 flex-1">
        <p className="numero mt-2 text-2xl font-semibold break-words">{novas}</p>
        <p className="mt-1 text-xs text-tinta-3">{novas ? t('inicio.cards.novidades.naoVistas', { count: novas }) : t('inicio.cards.novidades.emDia')}</p>
        <ul className="mt-3 -mx-2 flex flex-col border-t border-borda pt-2">
          {visiveis.slice(0, NOVIDADES_NO_CARD).map((n, k) => (
            <li key={n.id}>
              <button type="button" data-novidade-card={n.id} onClick={() => setAberto({ lista: visiveis, inicio: k })} className="flex w-full min-w-0 items-center gap-3 rounded-md px-2 py-1.5 text-left hover:bg-sup-2">
                <span className="min-w-0 flex-1">
                  <TextoCortado className="block text-sm font-medium text-tinta-1">{n.textos[idioma].titulo}</TextoCortado>
                  <span className="block text-xs text-tinta-3">{data(n.publicada_em)}</span>
                </span>
                {pendentes.has(n.id) && <Selo cor="acento">{t('inicio.cards.novidades.nova')}</Selo>}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <button type="button" onClick={() => setAberto({ lista: visiveis, inicio: 0 })} className="-mb-1 mt-2 flex items-center gap-1 self-end rounded-md px-1 py-1 text-xs font-medium text-acento-forte hover:underline">
        {t('inicio.cards.novidades.verTodas')}
        <ArrowRight className="size-3.5" aria-hidden />
      </button>
      {aberto && (
        <ModalNovidades
          lista={aberto.lista}
          inicio={aberto.inicio}
          aoFechar={({ vistas }) => {
            const novasVistas = vistas.filter((id) => pendentes.has(id));
            if (novasVistas.length) marcar.mutate(novasVistas);
            setAberto(null);
          }}
        />
      )}
    </article>
  );
}
